import './receipt.css';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexTransactionType} from '@/types';
import type {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';
import {capitalizeWords} from '@/utils/dashboard';
import {getReceiptTransactionFileName} from '@/utils/receipt';

import {ReceiptFooter} from './Footer';
import GenerateOnePageReceiptButton from './GenerateOnePageButton';
import ReceiptHeader from './Header';

export type ReceiptKind = 'transaction' | 'businessTransaction' | 'kyc';

type TransactionTypeForReceipt =
  AfriexTransactionType | BusinessTransactionType;

type ReceiptKindConfig = {
  title: () => string;
  fileName: (transactionType?: TransactionTypeForReceipt) => string;
  label: string;
  /** Omitted when the receipt's data comes from an endpoint open to all staff. */
  permission?: AfriexAdminPermission;
  // Distinct from the Details header's hook; see GenerateOnePageButton.
  downloadDataCy?: string;
};

const consumerTransactionReceipt: ReceiptKindConfig = {
  title: () => 'Remittance Transaction Receipt',
  fileName: (transactionType) => getReceiptTransactionFileName(transactionType),
  label: 'Download Receipt',
  permission: AfriexAdminPermission.TransactionRead,
  downloadDataCy: 'admin-receipt-download',
};

/**
 * Everything that differs by receipt kind, in one place. A consumer and a
 * merchant (business) transaction share a title, filename rule and download
 * hook, and differ only in the permission that gates the download: each is
 * the read permission of the endpoint that loaded the transaction.
 */
const RECEIPT_KINDS: Record<ReceiptKind, ReceiptKindConfig> = {
  transaction: consumerTransactionReceipt,
  businessTransaction: {
    ...consumerTransactionReceipt,
    permission: undefined,
  },
  kyc: {
    title: () => `${capitalizeWords('kyc')} Receipt`,
    fileName: () => 'kyc',
    label: 'Download KYC',
  },
};

/**
 * The kind's config. A value outside `ReceiptKind` cannot be passed by typed
 * code; if one arrives anyway it keeps the pre-table behaviour (a derived
 * title, no filename, and the KYC receipt's gate) rather than throwing.
 */
const configFor = (type: ReceiptKind): ReceiptKindConfig =>
  Object.hasOwn(RECEIPT_KINDS, type)
    ? RECEIPT_KINDS[type]
    : {
        title: () =>
          `${capitalizeWords(String(type).replace(/_/g, ' '))} Receipt`,
        fileName: () => '',
        label: 'Download Receipt',
      };

const Receipt = ({
  type,
  transactionType,
  scope,
  children,
}: {
  type: ReceiptKind;
  transactionType?: TransactionTypeForReceipt;
  /** The transaction or person id, named in the downloaded file. */
  scope?: string;
  children: React.ReactNode;
}) => {
  const config = configFor(type);
  const downloadButton = (
    <GenerateOnePageReceiptButton
      fileName={config.fileName(transactionType)}
      scope={scope}
      label={config.label}
      dataCy={config.downloadDataCy}
    />
  );

  return (
    <div className="flex flex-col gap-5 mt-5">
      <div className="text-right">
        {/* Generate Receipt Button */}
        {config.permission ? (
          <PermissionsProvider permission={config.permission}>
            {downloadButton}
          </PermissionsProvider>
        ) : (
          downloadButton
        )}
      </div>
      <div
        id="receipt-parent"
        data-cy="admin-receipt"
        className={`flex w-450 flex-col justify-center border border-receipt-line rounded-md dark:border-dark-borderMid`}
      >
        <ReceiptHeader />

        <div className="body mx-7">
          <p
            className="header font-bold text-sm text-cyan-600"
            data-cy="admin-receipt-title"
          >
            {config.title()}
          </p>

          {children}
        </div>

        <ReceiptFooter />
      </div>
    </div>
  );
};

Receipt.displayName = 'Receipt';
export default Receipt;
